import { useEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';
import { readSnap, writeSnap } from '../lib/rankAnim';

/**
 * AnimatedNumber — counts from a baseline up/down to `value` (a coin-tally
 * roll) and briefly flashes green (increase) / red (decrease).
 *
 * Baseline resolution (first match wins):
 *   1. `from`        — parent-managed baseline (e.g. previous points from a
 *                      leaderboard snapshot). Storage is NOT touched.
 *   2. `snapshotKey` — self-managed: reads the last-seen value from
 *                      localStorage and writes the new one when the roll ends.
 *   3. otherwise     — starts at `value` (no animation the very first time).
 */
export function AnimatedNumber({
  value,
  from,
  snapshotKey,
  format = (n) => n.toFixed(2),
  className = '',
  durationMs = 1000,
  flashColors = true,
  testid,
}) {
  const baseRef = useRef(null);
  if (baseRef.current === null) {
    if (typeof from === 'number' && Number.isFinite(from)) {
      baseRef.current = from;
    } else if (snapshotKey) {
      const s = readSnap(snapshotKey);
      baseRef.current = typeof s === 'number' && Number.isFinite(s) ? s : value;
    } else {
      baseRef.current = value;
    }
  }

  const [display, setDisplay] = useState(baseRef.current ?? 0);
  const [flash, setFlash] = useState(null); // 'up' | 'down' | null
  const targetRef = useRef(baseRef.current);

  useEffect(() => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    const start = targetRef.current;
    targetRef.current = value;
    if (start === value) {
      setDisplay(value);
      return;
    }
    if (flashColors && start != null) setFlash(value > start ? 'up' : 'down');
    let flashTimer = null;
    const controls = animate(start ?? value, value, {
      duration: durationMs / 1000,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(v),
      onComplete: () => {
        if (snapshotKey) writeSnap(snapshotKey, value);
        flashTimer = setTimeout(() => setFlash(null), 700);
      },
    });
    return () => {
      controls.stop();
      // 2026-08-12 (mobile-parity hardening) — clear the flash timer so a
      // lingering timeout can't setState after unmount.
      if (flashTimer) clearTimeout(flashTimer);
    };
  }, [value, durationMs, snapshotKey, flashColors]);

  const flashStyle =
    flash === 'up'
      ? { color: 'hsl(var(--chart-5))' }
      : flash === 'down'
      ? { color: 'rgb(239 68 68)' }
      : undefined;

  return (
    <span
      className={className}
      style={{ transition: 'color 320ms ease', ...(flashStyle || {}) }}
      data-testid={testid}
    >
      {format(display)}
    </span>
  );
}

export default AnimatedNumber;
